import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { open_api_with_cookies } from "../../../Utils/network";
import SearchBar from "../../../components/Search/SearchBar";
import FilterChip from "../../../components/Chip/FilterChip";
import DateRangeChip from "../../../components/Chip/DateRangeChip";
import LoadingOverlay from "../../../components/Loading/LoadingOverlay";
import "./AISessions.css";

const modelOptions = ["All Models", "claude-sonnet-4-6", "claude-opus-4-6"];
const statusOptions = ["All Status", "Active", "Inactive"];
const phaseOptions = ["All Phases", "empathy", "asking", "educating", "advising"];

const AISessions = () => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [modelFilter, setModelFilter] = useState("All Models");
  const [statusFilter, setStatusFilter] = useState("All Status");
  const [phaseFilter, setPhaseFilter] = useState("All Phases");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const createDateString = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${d.getFullYear()}-${month}-${day}`;
  };

  useEffect(() => {
    const fetchSessions = async () => {
      setIsLoading(true);
      try {
        let url = `/myburgoai/admin/sessions/?page=${page}&page_size=20`;
        if (search) url += `&search=${encodeURIComponent(search)}`;
        if (modelFilter !== "All Models") url += `&model=${modelFilter}`;
        if (statusFilter === "Active") url += `&active=true`;
        else if (statusFilter === "Inactive") url += `&active=false`;
        if (phaseFilter !== "All Phases") url += `&phase=${phaseFilter}`;
        if (startDate) url += `&start_date=${createDateString(startDate)}`;
        if (endDate) url += `&end_date=${createDateString(endDate)}`;

        const res = await open_api_with_cookies.get(url);
        setSessions(res.data.results || []);
        setTotalPages(res.data.total_pages || 1);
        setTotal(res.data.total || 0);
      } catch (error) {
        console.error("Error fetching sessions:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchSessions();
  }, [page, search, modelFilter, statusFilter, phaseFilter, startDate, endDate]);

  const handleSearch = (query) => {
    setSearch(query);
    setPage(1);
  };

  const handleDateChange = (start, end) => {
    setStartDate(start);
    setEndDate(end);
    setPage(1);
  };

  const handleRowClick = (sessionId) => {
    navigate(`/ai/sessions/${sessionId}`);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Never";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="ai-sessions">
      <div className="ai-sessions-header">
        <h2>AI Chat Sessions</h2>
        <span style={{ color: "#9197b3", fontSize: 14 }}>
          {total} total sessions
        </span>
      </div>

      <div className="ai-sessions-filters">
        <div className="filters-left">
          <FilterChip
            options={modelOptions}
            selected={modelFilter}
            onSelect={(v) => { setModelFilter(v); setPage(1); }}
          />
          <FilterChip
            options={statusOptions}
            selected={statusFilter}
            onSelect={(v) => { setStatusFilter(v); setPage(1); }}
          />
          <FilterChip
            options={phaseOptions}
            selected={phaseFilter}
            onSelect={(v) => { setPhaseFilter(v); setPage(1); }}
          />
          <DateRangeChip label="Date Range" onDateChange={handleDateChange} />
        </div>
        <SearchBar onSearch={handleSearch} />
      </div>

      {isLoading ? (
        <LoadingOverlay />
      ) : (
        <div className="ai-sessions-table">
          <div className="sessions-table-header">
            <div>User</div>
            <div>App Activity</div>
            <div>Title</div>
            <div>Messages</div>
            <div>Tokens</div>
            <div>Model</div>
            <div>Phase</div>
            <div>AI Last Active</div>
          </div>

          {sessions.length > 0 ? (
            sessions.map((s) => (
              <div
                key={s.session_id}
                className="sessions-table-row"
                onClick={() => handleRowClick(s.session_id)}
              >
                <div className="session-user-info">
                  <span className="session-user-name">{s.user_name}</span>
                  {s.user_email && (
                    <span className="session-user-contact">{s.user_email}</span>
                  )}
                  {s.user_phone && (
                    <span className="session-user-contact">
                      {[s.user_isd_code, s.user_phone].filter(Boolean).join(" ")}
                    </span>
                  )}
                </div>
                <div className="session-app-activity">
                  <span
                    className={`session-app-status ${
                      s.app_installed ? "installed" : "not-observed"
                    }`}
                  >
                    {s.app_installed ? "Installed" : "Not observed"}
                  </span>
                  <span className="session-app-time">
                    Used {formatDate(s.last_app_used_at)}
                  </span>
                  {s.last_app_opened_at && (
                    <span className="session-app-time">
                      Opened {formatDate(s.last_app_opened_at)}
                    </span>
                  )}
                </div>
                <div className="session-title" title={s.title}>
                  {s.title || "Untitled"}
                </div>
                <div>{s.total_messages}</div>
                <div>{(s.total_tokens_used || 0).toLocaleString()}</div>
                <div>
                  <span className="session-model-badge">
                    {s.ai_model ? s.ai_model.replace("claude-", "").replace("-4-6", "") : "—"}
                  </span>
                </div>
                <div>
                  <span className="session-phase-badge">
                    {s.conversation_phase || "—"}
                  </span>
                </div>
                <div>
                  <div>{formatDate(s.last_activity)}</div>
                  <div style={{ fontSize: 12, color: "#9197b3" }}>
                    {formatTime(s.last_activity)}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="sessions-empty">No sessions found.</div>
          )}

          {totalPages > 1 && (
            <div className="sessions-pagination">
              <span>
                Page {page} of {totalPages} ({total} total)
              </span>
              <div className="page-buttons">
                <button
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                >
                  Previous
                </button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (page <= 3) {
                    pageNum = i + 1;
                  } else if (page >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = page - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      className={page === pageNum ? "active" : ""}
                      onClick={() => setPage(pageNum)}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                <button
                  disabled={page >= totalPages}
                  onClick={() => setPage(page + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AISessions;
